import { useState, KeyboardEvent, ClipboardEvent } from 'react';
import { X, Mail } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface EmailChipsInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  className?: string;
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export function EmailChipsInput({ value, onChange, placeholder = "Ajouter un email...", className }: EmailChipsInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail) return;
    
    if (!isValidEmail(trimmedEmail)) {
      setError('Format d\'email invalide');
      return;
    }
    
    if (value.includes(trimmedEmail)) {
      setError('Cet email est déjà ajouté');
      return;
    }
    
    onChange([...value, trimmedEmail]);
    setInputValue('');
    setError('');
  };

  const removeEmail = (emailToRemove: string) => {
    onChange(value.filter(email => email !== emailToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Si le champ contient plusieurs emails séparés par des virgules, les traiter tous
      if (inputValue.includes(',')) {
        const emails = inputValue.split(',').map(e => e.trim()).filter(Boolean);
        emails.forEach(email => addEmail(email));
      } else {
        addEmail(inputValue);
      }
    } else if (e.key === ',') {
      e.preventDefault();
      // Ajouter l'email avant la virgule
      addEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeEmail(value[value.length - 1]);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    // Détecter les emails dans le texte collé
    // Supporte: virgules, points-virgules, espaces, retours à la ligne, tabulations
    const emailRegex = /[^\s,;\n\r\t]+@[^\s,;\n\r\t]+\.[^\s,;\n\r\t]+/g;
    const foundEmails = pastedText.match(emailRegex) || [];
    
    // Ajouter les emails valides et uniques
    const newEmails: string[] = [];
    foundEmails.forEach(email => {
      const trimmedEmail = email.trim().toLowerCase();
      if (isValidEmail(trimmedEmail) && !value.includes(trimmedEmail) && !newEmails.includes(trimmedEmail)) {
        newEmails.push(trimmedEmail);
      }
    });
    
    if (newEmails.length > 0) {
      onChange([...value, ...newEmails]);
      setError('');
    } else if (foundEmails.length > 0) {
      setError('Aucun email valide trouvé dans le texte collé');
    }
    
    setInputValue('');
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="min-h-[2.5rem] p-2 border border-input rounded-md bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <div className="flex flex-wrap gap-1 mb-1">
          {value.map((email, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              <Mail className="w-3 h-3 mr-1" />
              {email}
              <button
                type="button"
                onClick={() => removeEmail(email)}
                className="ml-1 hover:text-destructive"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
        <Input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError('');
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : ""}
          className="border-0 p-0 h-auto focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      <p className="text-xs text-muted-foreground">
        Appuyez sur Entrée ou virgule pour ajouter un email. Vous pouvez coller une liste d'emails (séparés par virgules ou retours à la ligne).
      </p>
    </div>
  );
}